import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../ui/dialog';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../ui/select';
import { XCircle } from 'lucide-react';

// Organization Modal
export function OrgModal({ 
  open, 
  onOpenChange, 
  orgForm, 
  setOrgForm, 
  editingOrg, 
  onSave 
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-md">
        <DialogHeader>
          <DialogTitle>{editingOrg ? 'Edit Organization' : 'Create Organization'}</DialogTitle>
          <DialogDescription>
            {editingOrg ? 'Update the organization details below.' : 'Fill in the details to register a new organization.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="form-field">
            <Label className="form-label">Legal Name *</Label>
            <Input
              value={orgForm.legal_name}
              onChange={(e) => setOrgForm({ ...orgForm, legal_name: e.target.value })}
              className="form-input rounded-none"
              data-testid="org-legal-name"
            />
          </div>
          <div className="form-field">
            <Label className="form-label">Display Name *</Label>
            <Input
              value={orgForm.display_name}
              onChange={(e) => setOrgForm({ ...orgForm, display_name: e.target.value })}
              className="form-input rounded-none"
              data-testid="org-display-name"
            />
          </div>
          <div className="form-field">
            <Label className="form-label">Contact Email *</Label>
            <Input
              type="email"
              value={orgForm.contact_email}
              onChange={(e) => setOrgForm({ ...orgForm, contact_email: e.target.value })}
              className="form-input rounded-none"
              data-testid="org-email"
            />
          </div>
          <div className="form-field">
            <Label className="form-label">Website</Label>
            <Input
              value={orgForm.website}
              onChange={(e) => setOrgForm({ ...orgForm, website: e.target.value })}
              className="form-input rounded-none"
              placeholder="https://"
              data-testid="org-website"
            />
          </div>
          {editingOrg && (
            <div className="form-field">
              <Label className="form-label">Status</Label>
              <Select value={orgForm.status} onValueChange={(v) => setOrgForm({ ...orgForm, status: v })}>
                <SelectTrigger className="rounded-none" data-testid="org-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none">Cancel</Button>
          <Button onClick={onSave} className="btn-primary rounded-none" data-testid="save-org-btn">
            {editingOrg ? 'Save Changes' : 'Create Organization'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Declaration Modal
export function CertModal({ 
  open, 
  onOpenChange, 
  certForm, 
  setCertForm, 
  organizations, 
  standards, 
  onSave 
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-md">
        <DialogHeader>
          <DialogTitle>Issue Declaration</DialogTitle>
          <DialogDescription>
            Issue a new accountability declaration to an organization for a specific standard.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="form-field">
            <Label className="form-label">Organization *</Label>
            <Select value={certForm.org_id} onValueChange={(v) => setCertForm({ ...certForm, org_id: v })}>
              <SelectTrigger className="rounded-none" data-testid="cert-org">
                <SelectValue placeholder="Select organization" />
              </SelectTrigger>
              <SelectContent>
                {organizations.filter(o => o.status === 'active').map(org => (
                  <SelectItem key={org.id} value={org.id}>{org.display_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="form-field">
            <Label className="form-label">Standard *</Label>
            <Select value={certForm.standard_id} onValueChange={(v) => setCertForm({ ...certForm, standard_id: v })}>
              <SelectTrigger className="rounded-none" data-testid="cert-standard">
                <SelectValue placeholder="Select standard" />
              </SelectTrigger>
              <SelectContent>
                {standards.filter(s => s.status === 'active').map(std => (
                  <SelectItem key={std.id} value={std.id}>{std.title} ({std.id})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="form-field">
            <Label className="form-label">Issue Date *</Label>
            <Input
              type="date"
              value={certForm.issue_date}
              onChange={(e) => setCertForm({ ...certForm, issue_date: e.target.value })}
              className="form-input rounded-none"
              data-testid="cert-issue-date"
            />
          </div>
          <div className="form-field">
            <Label className="form-label">Expiry Date *</Label>
            <Input
              type="date"
              value={certForm.expiry_date}
              onChange={(e) => setCertForm({ ...certForm, expiry_date: e.target.value })}
              className="form-input rounded-none"
              data-testid="cert-expiry-date"
            />
          </div>
          <div className="form-field">
            <Label className="form-label">Notes</Label>
            <Input
              value={certForm.notes}
              onChange={(e) => setCertForm({ ...certForm, notes: e.target.value })}
              className="form-input rounded-none"
              placeholder="Optional notes"
              data-testid="cert-notes"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none">Cancel</Button>
          <Button onClick={onSave} className="btn-primary rounded-none" data-testid="issue-cert-submit">
            Issue Declaration
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// User Modal
export function UserModal({ 
  open, 
  onOpenChange, 
  userForm, 
  setUserForm, 
  organizations, 
  onSave 
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-md">
        <DialogHeader>
          <DialogTitle>Create User</DialogTitle>
          <DialogDescription>
            Create a new user account with organization access or admin privileges.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="form-field">
            <Label className="form-label">Name *</Label>
            <Input
              value={userForm.name}
              onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
              className="form-input rounded-none"
              data-testid="user-name"
            />
          </div>
          <div className="form-field">
            <Label className="form-label">Email *</Label>
            <Input
              type="email"
              value={userForm.email}
              onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
              className="form-input rounded-none"
              data-testid="user-email"
            />
          </div>
          <div className="form-field">
            <Label className="form-label">Password *</Label>
            <Input
              type="password"
              value={userForm.password}
              onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
              className="form-input rounded-none"
              data-testid="user-password"
            />
          </div>
          <div className="form-field">
            <Label className="form-label">Role *</Label>
            <Select value={userForm.role} onValueChange={(v) => setUserForm({ ...userForm, role: v })}>
              <SelectTrigger className="rounded-none" data-testid="user-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="org_user">Organization User</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {userForm.role === 'org_user' && (
            <div className="form-field">
              <Label className="form-label">Organization *</Label>
              <Select value={userForm.org_id} onValueChange={(v) => setUserForm({ ...userForm, org_id: v })}>
                <SelectTrigger className="rounded-none" data-testid="user-org">
                  <SelectValue placeholder="Select organization" />
                </SelectTrigger>
                <SelectContent>
                  {organizations.filter(o => o.status === 'active').map(org => (
                    <SelectItem key={org.id} value={org.id}>{org.display_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none">Cancel</Button>
          <Button onClick={onSave} className="btn-primary rounded-none" data-testid="create-user-submit">
            Create User
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Revoke Modal
export function RevokeModal({ 
  open, 
  onOpenChange, 
  revokingCert, 
  revokeReason, 
  setRevokeReason, 
  onRevoke 
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-md">
        <DialogHeader>
          <DialogTitle className="text-red-600">Revoke Declaration</DialogTitle>
          <DialogDescription>
            This action will permanently revoke the declaration and is publicly visible.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="bg-red-50 border border-red-200 p-4">
            <p className="text-sm text-red-800">
              You are about to revoke declaration <strong>{revokingCert?.declaration_id}</strong>.
              This action will be logged and visible in the public registry.
            </p>
          </div>
          <div className="form-field">
            <Label className="form-label">Revocation Reason *</Label>
            <Textarea
              value={revokeReason}
              onChange={(e) => setRevokeReason(e.target.value)}
              className="form-input rounded-none"
              placeholder="Enter the reason for revocation..."
              rows={3}
              data-testid="revoke-reason"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none">Cancel</Button>
          <Button onClick={onRevoke} className="bg-red-600 hover:bg-red-700 text-white rounded-none" data-testid="confirm-revoke">
            Revoke Declaration
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Deny Application Modal
export function DenyModal({ 
  open, 
  onOpenChange, 
  denyingApp, 
  denyReason, 
  setDenyReason, 
  onDeny 
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-md">
        <DialogHeader>
          <DialogTitle className="text-red-600">Deny Application</DialogTitle>
          <DialogDescription>
            This will deny the application and initiate a refund if payment was made.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="bg-zinc-50 p-4 border border-zinc-200">
            <p className="text-sm font-medium">{denyingApp?.organization?.display_name}</p>
            <p className="text-sm text-zinc-500">{denyingApp?.organization?.legal_name}</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="deny_reason">Denial Reason *</Label>
            <Textarea
              id="deny_reason"
              placeholder="Provide a clear reason for denial (minimum 10 characters)..."
              value={denyReason}
              onChange={(e) => setDenyReason(e.target.value)}
              rows={4}
              data-testid="deny-reason-input"
            />
            <p className="text-xs text-zinc-500">This reason will be shared with the applicant.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="destructive" onClick={onDeny} data-testid="confirm-deny-btn">
            <XCircle className="w-4 h-4 mr-2" />
            Deny Application
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Password Reset Modal
export function PasswordModal({ 
  open, 
  onOpenChange, 
  mustResetPassword,
  passwordForm, 
  setPasswordForm, 
  onReset 
}) {
  return (
    <Dialog open={open} onOpenChange={(o) => !mustResetPassword && onOpenChange(o)}>
      <DialogContent className="rounded-none max-w-md">
        <DialogHeader>
          <DialogTitle>
            {mustResetPassword ? 'Password Reset Required' : 'Change Password'}
          </DialogTitle>
          <DialogDescription>
            {mustResetPassword ? 'You must reset your password to continue using the platform.' : 'Enter your current password and choose a new secure password.'}
          </DialogDescription>
        </DialogHeader>
        {mustResetPassword && (
          <div className="bg-amber-50 border border-amber-200 p-4 mb-4">
            <p className="text-sm text-amber-800">
              For security, you must reset your password before continuing.
            </p>
          </div>
        )}
        <div className="space-y-4 py-4">
          <div className="form-field">
            <Label className="form-label">Current Password *</Label>
            <Input
              type="password"
              value={passwordForm.currentPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
              className="form-input rounded-none"
              data-testid="current-password"
            />
          </div>
          <div className="form-field">
            <Label className="form-label">New Password * (min 12 characters)</Label>
            <Input
              type="password"
              value={passwordForm.newPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
              className="form-input rounded-none"
              data-testid="new-password"
            />
          </div>
          <div className="form-field">
            <Label className="form-label">Confirm New Password *</Label>
            <Input
              type="password"
              value={passwordForm.confirmPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
              className="form-input rounded-none"
              data-testid="confirm-password"
            />
          </div>
        </div>
        <DialogFooter>
          {!mustResetPassword && (
            <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none">Cancel</Button>
          )}
          <Button onClick={onReset} className="btn-primary rounded-none" data-testid="reset-password-btn">
            Reset Password
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
